/**
 * ListasLaterais — widgets "Últimas Defesas" e "Pesquisas Jurídicas" do
 * dashboard original: lista curta com estado vazio ilustrado + CTA.
 */

import {
  faBalanceScale,
  faPlus,
  faShieldAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { listarItens } from '../../../api/dashboard'
import { CardWidget } from './CardWidget'

function ListaCurta({
  titulo,
  subtitulo,
  icone,
  gradiente,
  corSubtitulo,
  dominio,
  vazioTitulo,
  vazioTexto,
  ctaRotulo,
  ctaUrl,
  corCta,
  corIconeVazio,
}: {
  titulo: string
  subtitulo: string
  icone: IconDefinition
  gradiente: string
  corSubtitulo: string
  dominio: string
  vazioTitulo: string
  vazioTexto: string
  ctaRotulo: string
  ctaUrl: string
  corCta: string
  corIconeVazio: string
}) {
  const consulta = useQuery({
    queryKey: ['dashboard-itens-widget', dominio],
    queryFn: () => listarItens({ dominio, porPagina: 5 }),
  })

  return (
    <CardWidget
      titulo={titulo}
      subtitulo={subtitulo}
      icone={icone}
      gradiente={gradiente}
      corSubtitulo={corSubtitulo}
    >
      <div className="p-4">
        {consulta.isLoading && <p className="py-6 text-center text-sm text-slate-500">Carregando…</p>}
        {consulta.data && consulta.data.itens.length > 0 && (
          <ul className="space-y-2">
            {consulta.data.itens.map((item) => (
              <li key={item.id}>
                <a
                  href={item.url}
                  className="block rounded-xl border border-slate-100 p-3 transition-all hover:border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-dark-hover"
                >
                  <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                    {item.titulo}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">{item.data.slice(0, 10)}</p>
                </a>
              </li>
            ))}
          </ul>
        )}
        {consulta.data && consulta.data.itens.length === 0 && (
          <div className="py-6 text-center">
            <div className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full ${corIconeVazio}`}>
              <FontAwesomeIcon icon={icone} className="text-xl" />
            </div>
            <h3 className="mb-1 text-base font-semibold text-slate-700 dark:text-slate-200">
              {vazioTitulo}
            </h3>
            <p className="mb-4 text-sm text-slate-500">{vazioTexto}</p>
            <a
              href={ctaUrl}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${corCta}`}
            >
              <FontAwesomeIcon icon={faPlus} className="text-xs" />
              {ctaRotulo}
            </a>
          </div>
        )}
      </div>
    </CardWidget>
  )
}

export function UltimasDefesas() {
  return (
    <ListaCurta
      titulo="Últimas Defesas"
      subtitulo="Defesas geradas recentemente"
      icone={faShieldAlt}
      gradiente="from-blue-600 to-indigo-600"
      corSubtitulo="text-blue-100"
      dominio="defesa"
      vazioTitulo="Nenhuma defesa gerada"
      vazioTexto="Gere defesas para vê-las aqui"
      ctaRotulo="Nova Defesa"
      ctaUrl="/juridico/defesa"
      corCta="bg-blue-600 hover:bg-blue-700"
      corIconeVazio="bg-blue-50 text-blue-500 dark:bg-blue-900/30"
    />
  )
}

export function PesquisasJuridicas() {
  return (
    <ListaCurta
      titulo="Pesquisas Jurídicas"
      subtitulo="Consultas salvas"
      icone={faBalanceScale}
      gradiente="from-purple-600 to-violet-600"
      corSubtitulo="text-purple-100"
      dominio="pesquisa_juridica"
      vazioTitulo="Nenhuma pesquisa salva"
      vazioTexto="Salve consultas para consultá-las aqui"
      ctaRotulo="Nova Pesquisa"
      ctaUrl="/legal_research"
      corCta="bg-purple-600 hover:bg-purple-700"
      corIconeVazio="bg-purple-50 text-purple-500 dark:bg-purple-900/30"
    />
  )
}
